import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { ROUTES, URN_QDOS_S3 } from '../../helpers/constans';
import { shareActions } from '../../redux/slices/share.slice';
import { RootState } from '../../redux/store';

type TReactionType = 'like' | 'love' | 'fire' | 'shock' | 'effort';

const REACTIONS: Array<{
  key: TReactionType;
  label: string;
  icon: ReactElement;
}> = [
  { key: 'like', label: 'Like', icon: <ThumbUpAltIcon fontSize="small" /> },
  { key: 'love', label: 'Love', icon: <FavoriteIcon fontSize="small" /> },
  { key: 'fire', label: 'On Fire', icon: <LocalFireDepartmentIcon fontSize="small" /> },
  { key: 'shock', label: 'Shock', icon: <EmojiEmotionsIcon fontSize="small" /> },
  { key: 'effort', label: 'Effort', icon: <FitnessCenterIcon fontSize="small" /> },
];

const TeamShare = (): ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [reactionDialogItem, setReactionDialogItem] = useState<any | null>(null);
  const [portraitMap, setPortraitMap] = useState<Record<string, boolean>>({});

  const shareState = useSelector((state: RootState) => state.share);

  const teamShare = useMemo(() => {
    if (!Array.isArray(shareState?.teamShare)) return [];
    return shareState.teamShare;
  }, [shareState?.teamShare]);

  const teamShareHistory = useMemo(() => {
    return shareState?.teamShareHistory || { currentEvent: null, previousEvents: [] };
  }, [shareState?.teamShareHistory]);

  useEffect(() => {
    dispatch(shareActions.getTeamShareHistory());
  }, [dispatch]);

  useEffect(() => {
    if (eventId) {
      dispatch(shareActions.getTeamShare(eventId));
      return;
    }

    if (teamShareHistory?.currentEvent?.qdosEventId) {
      navigate(
        generatePath(ROUTES.teamShare, {
          eventId: teamShareHistory.currentEvent.qdosEventId,
        }),
        { replace: true },
      );
    }
  }, [dispatch, eventId, navigate, teamShareHistory]);

  const getMediaUrl = (value?: string): string => {
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    return `${URN_QDOS_S3}${value}`;
  };

  const requestFullscreen = (element: HTMLElement): void => {
    const anyElement = element as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
      msRequestFullscreen?: () => Promise<void> | void;
    };

    if (anyElement.requestFullscreen) {
      void anyElement.requestFullscreen();
      return;
    }

    if (anyElement.webkitRequestFullscreen) {
      void anyElement.webkitRequestFullscreen();
      return;
    }

    if (anyElement.msRequestFullscreen) {
      void anyElement.msRequestFullscreen();
    }
  };

  const getImageKey = (item: any): string => {
    return item?._id || item?.imageId || item?.alternativeId || 'unknown-image';
  };

  const handleImageLoad =
    (key: string) =>
    (event: React.SyntheticEvent<HTMLImageElement>): void => {
      const img = event.currentTarget;
      const isPortrait = img.naturalHeight > img.naturalWidth;

      setPortraitMap((prev) => {
        if (prev[key] === isPortrait) return prev;
        return { ...prev, [key]: isPortrait };
      });
    };

  const getReactionCounts = (item: any) => ({
    like: item?.reactionCounts?.like || 0,
    love: item?.reactionCounts?.love || 0,
    fire: item?.reactionCounts?.fire || 0,
    shock: item?.reactionCounts?.shock || 0,
    effort: item?.reactionCounts?.effort || 0,
  });

  const openReactionDialog = (item: any, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setReactionDialogItem(item);
  };

  const handleSetReaction = (item: any, reactionType: TReactionType) => {
    const currentEventId = eventId || item?.qdosEventId;
    if (!currentEventId) return;

    dispatch(
      shareActions.setShareReaction({
        shareItemId: item._id,
        reactionType,
        shareType: 'team',
        eventId: currentEventId,
      }),
    );

    setReactionDialogItem(null);
  };

  const handleRemoveReaction = (item: any) => {
    const currentEventId = eventId || item?.qdosEventId;
    if (!currentEventId) return;

    dispatch(
      shareActions.removeShareReaction({
        shareItemId: item._id,
        shareType: 'team',
        eventId: currentEventId,
      }),
    );

    setReactionDialogItem(null);
  };

  const handleRemoveShareItem = (item: any, event: React.MouseEvent) => {
    event.stopPropagation();

    const currentEventId = eventId || item?.qdosEventId;
    dispatch(
      shareActions.removeShareItem({
        id: item._id,
        shareType: 'team',
        eventId: currentEventId,
      }),
    );
  };

  const renderBellButton = (item: any): ReactElement => {
    return (
      <IconButton
        onClick={(event) => openReactionDialog(item, event)}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2,
          backgroundColor: 'rgba(0,0,0,0.55)',
          color: '#fff',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.72)',
          },
        }}
      >
        <NotificationsNoneIcon fontSize="small" />
      </IconButton>
    );
  };

  const renderReactionSummary = (item: any): ReactElement => {
    const counts = getReactionCounts(item);

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', mt: '0.5rem' }}>
        {REACTIONS.map((reaction) => (
          <Box
            key={reaction.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              px: '0.55rem',
              py: '0.3rem',
              borderRadius: '999px',
              backgroundColor:
                item?.myReaction === reaction.key ? 'rgba(25,118,210,0.10)' : '#f5f5f5',
              border:
                item?.myReaction === reaction.key
                  ? '1px solid rgba(25,118,210,0.35)'
                  : '1px solid #e0e0e0',
            }}
          >
            {reaction.icon}
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
              {counts[reaction.key]}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderMedia = (item: any, enlarged = false): ReactElement => {
    const imageUrl = getMediaUrl(item?.imageId);
    const videoUrl = getMediaUrl(item?.videoUrl);
    const imageKey = getImageKey(item);
    const isPortrait = portraitMap[imageKey];

    if (videoUrl) {
      return (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#000',
          }}
        >
          {!enlarged && renderBellButton(item)}

          <video
            controls
            style={{
              display: 'block',
              width: '100%',
              maxHeight: enlarged ? '75vh' : '260px',
              objectFit: 'contain',
              backgroundColor: '#000',
            }}
          >
            <source src={videoUrl} />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }

    if (imageUrl) {
      return (
        <Box sx={{ position: 'relative' }}>
          {!enlarged && renderBellButton(item)}

          <Box
            component="img"
            src={imageUrl}
            alt={item?.eventTitle || 'Team Share media'}
            onLoad={handleImageLoad(imageKey)}
            onClick={
              enlarged
                ? (event: React.MouseEvent<HTMLElement>) => {
                    event.stopPropagation();
                    requestFullscreen(event.currentTarget);
                  }
                : undefined
            }
            sx={{
              width: '100%',
              height: enlarged ? 'auto' : '220px',
              maxHeight: enlarged ? '75vh' : '220px',
              objectFit: enlarged ? 'contain' : isPortrait ? 'contain' : 'cover',
              borderRadius: '8px',
              display: 'block',
              backgroundColor: '#f5f5f5',
              cursor: enlarged ? 'zoom-in' : 'default',
            }}
          />

          {enlarged && (
            <Typography sx={{ mt: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
              Click image for full screen
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: enlarged ? '260px' : '220px',
          borderRadius: '8px',
          border: '1px dashed #bbb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '1rem',
          color: '#777',
          textAlign: 'center',
          backgroundColor: '#fafafa',
        }}
      >
        {!enlarged && renderBellButton(item)}
        <Typography>{item?.alternativeId || 'No media preview available'}</Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100%' }}>
      <Box
        sx={{
          width: 240,
          borderRight: '1px solid #e0e0e0',
          p: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        <Typography variant="h5" sx={{ mb: '0.5rem' }}>
          Team Share
        </Typography>

        {teamShareHistory?.currentEvent && (
          <Button
            variant="contained"
            onClick={() =>
              navigate(
                generatePath(ROUTES.teamShare, {
                  eventId: teamShareHistory.currentEvent.qdosEventId,
                }),
              )
            }
          >
            Current Event
          </Button>
        )}

        <Typography variant="subtitle1" sx={{ mt: '1rem' }}>
          Previous Events
        </Typography>

        {(teamShareHistory?.previousEvents || []).map((event: any) => (
          <Button
            key={event.eventId}
            variant="text"
            onClick={() =>
              navigate(
                generatePath(ROUTES.teamShare, {
                  eventId: event.qdosEventId,
                }),
              )
            }
            sx={{ justifyContent: 'flex-start' }}
          >
            {event.eventTitle}
          </Button>
        ))}
      </Box>

      <Box sx={{ flex: 1, p: '2rem' }}>
        <Typography variant="h4" sx={{ mb: '1.5rem' }}>
          Team Share
        </Typography>

        {!teamShare.length ? (
          <Typography sx={{ color: '#666' }}>No Team Share media found for this event.</Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: '1rem',
              gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))',
            }}
          >
            {teamShare.map((item: any) => (
              <Box
                key={item._id}
                onClick={() => setSelectedItem(item)}
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: '12px',
                  p: '1rem',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <Typography sx={{ fontWeight: 700, mb: '0.5rem' }}>
                  {item.eventTitle || 'Team Share item'}
                </Typography>

                <Typography sx={{ mb: '0.75rem' }}>
                  {item.description || 'No description'}
                </Typography>

                <Box sx={{ mb: '0.75rem' }}>{renderMedia(item)}</Box>

                {renderReactionSummary(item)}

                {item.canRemove && (
                  <Button
                    size="small"
                    color="error"
                    sx={{ mt: '0.75rem' }}
                    onClick={(event) => handleRemoveShareItem(item, event)}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Dialog
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        maxWidth="md"
        fullWidth
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: '1rem',
            pt: '1rem',
          }}
        >
          <Typography variant="h6">
            {selectedItem?.eventTitle || 'Team Share item'}
          </Typography>
          <IconButton onClick={() => setSelectedItem(null)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent>
          {selectedItem && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {renderMedia(selectedItem, true)}

              <Typography>{selectedItem.description || 'No description'}</Typography>

              {renderReactionSummary(selectedItem)}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  startIcon={<NotificationsNoneIcon />}
                  onClick={() => setReactionDialogItem(selectedItem)}
                >
                  Reactions
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(reactionDialogItem)}
        onClose={() => setReactionDialogItem(null)}
        maxWidth="sm"
        fullWidth
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: '1rem',
            pt: '1rem',
          }}
        >
          <Typography variant="h6">React to this image</Typography>
          <IconButton onClick={() => setReactionDialogItem(null)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent>
          {reactionDialogItem && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Typography sx={{ color: '#666' }}>
                {reactionDialogItem.canReact
                  ? 'Choose one reaction. Selecting a different one will replace your current reaction.'
                  : 'You cannot react to your own shared media, but you can still view all totals.'}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                {REACTIONS.map((reaction) => {
                  const count = getReactionCounts(reactionDialogItem)[reaction.key];
                  const isSelected = reactionDialogItem?.myReaction === reaction.key;

                  return (
                    <Button
                      key={reaction.key}
                      variant={isSelected ? 'contained' : 'outlined'}
                      disabled={!reactionDialogItem.canReact}
                      onClick={() => handleSetReaction(reactionDialogItem, reaction.key)}
                      sx={{
                        justifyContent: 'space-between',
                        textTransform: 'none',
                        py: '0.85rem',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {reaction.icon}
                        <span>{reaction.label}</span>
                      </Box>
                      <span>{count}</span>
                    </Button>
                  );
                })}
              </Box>

              {reactionDialogItem?.myReaction && reactionDialogItem.canReact && (
                <Button
                  color="inherit"
                  variant="text"
                  onClick={() => handleRemoveReaction(reactionDialogItem)}
                >
                  Remove my reaction
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TeamShare;